import React, { useEffect } from 'react';
import { useExpEnabled, useUser } from '@reverbdotcom/commons/src/user_hooks';
import experiments from '@reverbdotcom/commons/src/experiments.json';
import { core_apimessages_CartItemAttributes as CartItemAttributes } from '../schemas/rql';
import LoggedOutApplePayButton from './logged_out_apple_pay_button';
import LoggedInApplePayButton from './apple_pay_button';
import { inMobileBrowser } from '@reverbdotcom/commons/src/user_context_helpers';
import { useApplePay } from './apple_pay_hook';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';

interface Props {
  listingCurrencyCode: string;
  paymentMethodsConfig: string;
  payButtonType?: 'checkout' | 'plain';
  cartItemAttributes?: CartItemAttributes[];
}

export function useCheckApplePaySupport(paymentMethodsConfig, listingCurrencyCode) {
  const [isSupported, setIsSupported] = React.useState(false);
  const [supportCheckLoading, setSupportCheckLoading] = React.useState(true);
  const user = useUser();
  const { isAvailable, loading } = useApplePay({
    paymentMethodsConfig,
    listingCurrencyCode,
  });

  useEffect(() => {
    async function checkApplePaySupport() {
      if (['US', 'GB', 'CA'].includes(user.countryCode)) {
        setIsSupported(isAvailable);
        if (!loading) setSupportCheckLoading(false);
      } else if (window.ApplePaySession) {
        const { paymentMethods } = JSON.parse(paymentMethodsConfig);
        const { merchantId } = paymentMethods.find((method) => method.type === 'applepay').configuration;
        setIsSupported(await ApplePaySession.canMakePaymentsWithActiveCard(merchantId));
        setSupportCheckLoading(false);
      } else {
        setSupportCheckLoading(false);
      }
    }

    checkApplePaySupport();
  }, [paymentMethodsConfig, user.countryCode, isAvailable, loading]);

  return [isSupported, supportCheckLoading];
}

export default function ApplePayButton(props: Props) {
  const { paymentMethodsConfig, listingCurrencyCode, payButtonType, cartItemAttributes } = props;
  const user = useUser();

  const [isAvailable, loading] = useCheckApplePaySupport(paymentMethodsConfig, listingCurrencyCode);

  const isApplePayIntlExpEnabled = useExpEnabled(experiments.APPLE_PAY_INTL_ACTIVE_CARD);
  const isCountryCodeGB = user.countryCode === 'GB';
  const showOnMobile = ['US', 'CA'].includes(user.countryCode) && inMobileBrowser(user);
  const showApplePay = isAvailable && (showOnMobile || isCountryCodeGB || isApplePayIntlExpEnabled);
  const trackedExperiments = [];

  if (!['US', 'GB', 'CA'].includes(user.countryCode) && isAvailable) {
    trackedExperiments.push(experiments.APPLE_PAY_INTL_ACTIVE_CARD);
  }

  useViewTracking({
    componentName: user.loggedOut ? 'LoggedOutApplePayButton' : 'ApplePayButton',
    eventName: MParticleEventName.ApplePayOffered,
    applePayAvailableForDevice: isAvailable,
    experiments: formatExperimentsForEventAttributes(user, trackedExperiments),
  }, !loading);

  if (!showApplePay) {
    return null;
  }

  if (user.loggedOut) {
    return (
      <LoggedOutApplePayButton
        data-logged-out-apple-pay-button
        paymentMethodsConfig={paymentMethodsConfig}
        listingCurrencyCode={listingCurrencyCode}
        cartItemAttributes={cartItemAttributes}
        payButtonType={payButtonType}
      />
    );
  }

  return (
    <LoggedInApplePayButton
      data-logged-in-apple-pay-button
      paymentMethodsConfig={paymentMethodsConfig}
      listingCurrencyCode={listingCurrencyCode}
      payButtonType={payButtonType}
    />
  );
}
